import { motion } from 'framer-motion';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Smartphone, Monitor, Tablet, Laptop } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DeviceData } from '@/hooks/useQRAnalytics';

interface DeviceBreakdownProps {
  devices: DeviceData[];
  operatingSystems: DeviceData[];
  browsers: DeviceData[];
}

const DEVICE_COLORS = [
  'hsl(var(--primary))',
  'hsl(280, 85%, 60%)',
  'hsl(320, 80%, 55%)',
  'hsl(200, 85%, 55%)',
  'hsl(150, 80%, 45%)',
];

const getDeviceIcon = (device: string) => {
  const lower = device.toLowerCase();
  if (lower.includes('mobile')) return Smartphone;
  if (lower.includes('tablet')) return Tablet;
  if (lower.includes('desktop')) return Monitor;
  return Laptop;
};

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
        <p className="text-sm font-medium">{payload[0].name}</p>
        <p className="text-sm text-muted-foreground">
          {payload[0].value} scans ({payload[0].payload.percentage}%)
        </p>
      </div>
    );
  }
  return null;
};

export function DeviceBreakdown({ devices, operatingSystems, browsers }: DeviceBreakdownProps) {
  const pieData = devices.map((d, i) => ({
    ...d,
    fill: DEVICE_COLORS[i % DEVICE_COLORS.length],
  }));

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      className="surface-elevated rounded-xl p-6"
    >
      <div className="flex items-center gap-2 mb-6">
        <Smartphone className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">Device & Platform</h3>
      </div>

      <Tabs defaultValue="devices">
        <TabsList className="mb-4">
          <TabsTrigger value="devices">Devices</TabsTrigger>
          <TabsTrigger value="os">Operating Systems</TabsTrigger>
          <TabsTrigger value="browsers">Browsers</TabsTrigger>
        </TabsList>

        <TabsContent value="devices">
          {devices.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Smartphone className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No device data available yet</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="count"
                      nameKey="name"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-3">
                {devices.map((device, index) => {
                  const Icon = getDeviceIcon(device.name);
                  return (
                    <div key={device.name} className="flex items-center gap-3">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: DEVICE_COLORS[index % DEVICE_COLORS.length] }}
                      />
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <span className="flex-1 text-sm">{device.name}</span>
                      <span className="text-sm font-medium">{device.percentage}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="os">
          {operatingSystems.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Monitor className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No OS data available yet</p>
            </div>
          ) : (
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={operatingSystems.slice(0, 6)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis type="category" dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} width={80} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </TabsContent>

        <TabsContent value="browsers">
          {browsers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Monitor className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No browser data available yet</p>
            </div>
          ) : (
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={browsers.slice(0, 5).map((b, i) => ({ ...b, fill: DEVICE_COLORS[i] }))}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="count"
                    nameKey="name"
                    label={({ name, percentage }) => `${name} ${percentage}%`}
                    labelLine={false}
                  >
                    {browsers.slice(0, 5).map((_, index) => (
                      <Cell key={`cell-${index}`} fill={DEVICE_COLORS[index % DEVICE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </motion.div>
  );
}
